import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export interface WorkflowTemplate {
  id: number;
  name: string;
  description: string | null;
  config: Record<string, unknown>;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  campaigns_count: number;
}

export interface WorkflowTemplateCreate {
  name: string;
  description?: string | null;
  config: Record<string, unknown>;
  is_default?: boolean;
  is_active?: boolean;
}

export interface WorkflowTemplateUpdate {
  name?: string | null;
  description?: string | null;
  config?: Record<string, unknown> | null;
  is_default?: boolean | null;
  is_active?: boolean | null;
}

async function readJson<T>(res: Response | undefined): Promise<T> {
  if (!res) throw new Error("Pas de reponse du serveur.");
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) {
        detail =
          typeof body.detail === "string"
            ? body.detail
            : JSON.stringify(body.detail);
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export async function listWorkflowTemplates(
  navigate: NavigateFunction,
): Promise<WorkflowTemplate[]> {
  const res = await apiFetch(
    `${API_URL}/admin/workflow-templates`,
    { method: "GET" },
    navigate,
  );
  return readJson<WorkflowTemplate[]>(res);
}

export async function getWorkflowTemplate(
  id: number,
  navigate: NavigateFunction,
): Promise<WorkflowTemplate> {
  const res = await apiFetch(
    `${API_URL}/admin/workflow-templates/${id}`,
    { method: "GET" },
    navigate,
  );
  return readJson<WorkflowTemplate>(res);
}

export async function createWorkflowTemplate(
  payload: WorkflowTemplateCreate,
  navigate: NavigateFunction,
): Promise<WorkflowTemplate> {
  const res = await apiFetch(
    `${API_URL}/admin/workflow-templates`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  return readJson<WorkflowTemplate>(res);
}

export async function updateWorkflowTemplate(
  id: number,
  payload: WorkflowTemplateUpdate,
  navigate: NavigateFunction,
): Promise<WorkflowTemplate> {
  const res = await apiFetch(
    `${API_URL}/admin/workflow-templates/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  return readJson<WorkflowTemplate>(res);
}

export async function deleteWorkflowTemplate(
  id: number,
  navigate: NavigateFunction,
): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/admin/workflow-templates/${id}`,
    { method: "DELETE" },
    navigate,
  );
  if (!res?.ok) {
    throw new Error(`HTTP ${res?.status ?? "?"}`);
  }
}
